import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ProvedorSessao, useSessao } from './auth/SessaoContexto.js';
import { Shell } from './layout/Shell.js';
import { Login } from './paginas/Login.js';
import { Blocos } from './paginas/Blocos.js';
import { Dashboard } from './paginas/Dashboard.js';
import { GerenciarIps } from './paginas/GerenciarIps.js';
import { Servidores } from './paginas/infra/Servidores.js';
import { Perfil } from './paginas/Perfil.js';
import { Logs } from './paginas/Logs.js';
import { Operadoras } from './paginas/Operadoras.js';
import { Solicitacoes } from './paginas/Solicitacoes.js';
import { Sincronizacao } from './paginas/Sincronizacao.js';
import { Templates } from './paginas/Templates.js';
import { Configuracoes } from './paginas/Configuracoes.js';
import { Dominios } from './paginas/Dominios.js';
import { Tokens } from './paginas/Tokens.js';
import { Usuarios } from './paginas/Usuarios.js';
import { Zonas } from './paginas/Zonas.js';
import { RegistrosPtr } from './paginas/ptr/RegistrosPtr.js';
import { PortalShell } from './portal/PortalShell.js';
import { MeusIps } from './portal/MeusIps.js';
import { MinhasSolicitacoes } from './portal/MinhasSolicitacoes.js';
import { Delegacao } from './portal/Delegacao.js';
import { PerfilPortal } from './portal/PerfilPortal.js';
import { DominiosPortal } from './portal/DominiosPortal.js';
import { ApiError } from './api/cliente.js';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      // Não reprocessa 401 nem 403: repetir uma requisição sem autorização não
      // muda o resultado e só atrasa a mensagem na tela.
      retry: (falhas, erro) => {
        if (erro instanceof ApiError && (erro.status === 401 || erro.status === 403)) return false;
        return falhas < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function Rotas() {
  const { usuario, carregando } = useSessao();

  if (carregando) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <span
          className="size-7 animate-spin rounded-full border-2 border-marca border-t-transparent"
          role="status"
          aria-label="Carregando"
        />
      </div>
    );
  }

  if (!usuario) return <Login />;

  // O tipo do usuário decide qual aplicação ele vê. Um único deploy serve aos
  // dois: a mesma sessão, o mesmo isolamento por tenant no servidor, e nenhuma
  // rota do painel alcançável por quem é cliente — o roteador nem as monta.
  if (usuario.tipo === 'CLIENTE') {
    return (
      <Routes>
        <Route element={<PortalShell />}>
          <Route path="/" element={<MeusIps />} />
          <Route path="/solicitacoes" element={<MinhasSolicitacoes />} />
          <Route path="/delegacao" element={<Delegacao />} />
          <Route path="/dominios" element={<DominiosPortal />} />
          <Route path="/perfil" element={<PerfilPortal />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/blocos" element={<Blocos />} />
        {/* O editor de PTR pertence a um bloco: o endereço da tela precisa
            dizer de qual, para o operador poder compartilhar o link. */}
        <Route path="/blocos/:blocoId/ptr" element={<RegistrosPtr />} />
        <Route path="/ips" element={<GerenciarIps />} />
        <Route path="/operadoras" element={<Operadoras />} />
        <Route path="/zonas" element={<Zonas />} />
        <Route path="/solicitacoes" element={<Solicitacoes />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/servidores" element={<Servidores />} />
        <Route path="/sincronizacao" element={<Sincronizacao />} />
        <Route path="/tokens" element={<Tokens />} />
        <Route path="/configuracoes" element={<Configuracoes />} />
        <Route path="/dominios" element={<Dominios />} />
        <Route path="/logs" element={<Logs />} />
        <Route path="/usuarios" element={<Usuarios />} />
        <Route path="/perfil" element={<Perfil />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <ProvedorSessao>
          <Rotas />
        </ProvedorSessao>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
